import { observer } from 'mobx-react-lite';
import { TFilterRenderer } from './types';
import { useResponsiveTableContext } from '@apia/table';
import { ApiaUtil, DateInput } from '@apia/components';
import { getDateFormat, TApiaFilter, uniqueId } from '@apia/util';
import dayjs from 'dayjs';
import FilterLabel from '../FilterLabel';
import { type HTMLAttributes, useState } from 'react';

const dateFormat = getDateFormat();
function controlDatesOrder(first: string, last: string) {
  if (!first || !last) return true;

  if (dayjs(last, dateFormat).isBefore(dayjs(first, dateFormat))) {
    return false;
  }
  return true;
}

export const DateFilterRenderer: TFilterRenderer = observer(({ state }) => {
  const { onFilterBlur, onFilterChange } = useResponsiveTableContext();
  const [dateFromHasError, setDateFromHasError] = useState(false);
  const [dateToHasError, setDateToHasError] = useState(false);

  const idFrom = uniqueId('date_from_');
  const idTo = uniqueId('date_to_');

  const notifyDateError = (
    error: string,
    setHasError: (hasError: boolean) => void,
  ) => {
    setHasError(true);
    ApiaUtil.instance.notifications.notify({
      message: error,
      type: 'warning',
    });
  };

  return (
    <>
      <FilterLabel state={state} isDate />
      <DateInput
        id={idFrom}
        className={`filter__query date_from ${
          dateFromHasError ? 'field__withError' : ''
        }`}
        props={
          {
            'data-query-filter-id': state.id,
            'data-query-filter-part': 'value',
          } as HTMLAttributes<HTMLInputElement>
        }
        sx={{
          '&.filter__query.filter__query:not([disabled])': {
            borderLeft: state.required ? '4px solid' : undefined,
            borderLeftColor: state.required ? 'palette.error.main' : undefined,
          },
        }}
        disabled={state.readonly}
        value={state.value}
        onChange={async (date) => {
          const result = controlDatesOrder(
            date ?? '',
            state.filterToValue ?? '',
          );
          if (result) {
            state.value = date ?? '';
            setDateFromHasError(false);
            onFilterChange?.(state as any as TApiaFilter);
            return null;
          }

          return window.MSG_FEC_FIN_MAY_FEC_INI;
        }}
        onError={(error) => {
          notifyDateError(error, setDateFromHasError);
        }}
        onBlur={() => {
          if (dateFromHasError) {
            state.value = '';
            setDateFromHasError(false);
            onFilterChange?.(state as any as TApiaFilter);
          }
          onFilterBlur?.({
            ...state,
            currentValue: state.value,
          } as TApiaFilter);
        }}
      />
      {(state?.isAdditional || state.group) && state?.filterToId && (
        <>
          <FilterLabel state={state} isDate />
          <DateInput
            id={idTo}
            className={`filter__query date_to ${
              dateToHasError ? 'field__withError' : ''
            }`}
            props={
              {
                'data-query-filter-id': state.id,
                'data-query-filter-part': 'filterToValue',
              } as HTMLAttributes<HTMLInputElement>
            }
            sx={{
              '&.filter__query.filter__query:not([disabled])': {
                borderLeft: state.required ? '4px solid' : undefined,
                borderLeftColor: state.required
                  ? 'palette.error.main'
                  : undefined,
              },
            }}
            disabled={state.readonly}
            value={state.filterToValue}
            onChange={async (date) => {
              const result = controlDatesOrder(state.value, date ?? '');
              if (result) {
                state.filterToValue = date ?? '';
                setDateToHasError(false);
                onFilterChange?.(state as any as TApiaFilter);
                return null;
              }

              return window.MSG_FEC_FIN_MAY_FEC_INI;
            }}
            onError={(error) => {
              notifyDateError(error, setDateToHasError);
            }}
            onBlur={() => {
              if (dateToHasError) {
                state.filterToValue = '';
                setDateToHasError(false);
                onFilterChange?.(state as any as TApiaFilter);
              }
              onFilterBlur?.({
                ...state,
                currentValue: state.filterToValue,
              } as TApiaFilter);
            }}
          />
        </>
      )}
    </>
  );
});
